import * as Y from "yjs";
import { buildRoomName, type RoomParts } from "@/collab/roomName";
import { parseTrustedBindingSnapshot, type TrustedPresenceBinding } from "@/collab/trustedPresenceBindings";

export interface SessionProvider {
    destroy(): void;
    setAwarenessField(key: string, value: unknown): void;
    awareness: { clientID: number; getStates(): Map<number, Record<string, unknown>> } | null;
    /**
     * F1: real HocuspocusProvider exposes this as a getter (`unsyncedChanges > 0`).
     * `unsyncedChanges` is a counter incremented in `documentUpdateHandler` for
     * every locally-originated ydoc update (the provider's `document.on("update",
     * ...)` listener is registered unconditionally at construction, so this
     * increments even while fully offline/never-connected) and decremented only
     * on the server's SyncStatus ACK (see @hocuspocus/provider's `synced`
     * JSDoc: "this does not mean all updates from the client have been
     * persisted to the backend. For this, use `hasUnsyncedChanges`"). Optional
     * so existing test fakes that don't implement it stay valid (treated as
     * "no unsynced changes").
     */
    hasUnsyncedChanges?: boolean;
}
export type SessionStatus = "connecting" | "connected" | "offline";
export interface ManagedSession {
    roomName: string;
    ydoc: Y.Doc;
    provider: SessionProvider;
    status: SessionStatus;
    synced: boolean;
    everSynced: boolean;
    trustedBindings: ReadonlyMap<number, TrustedPresenceBinding>;
}
export interface SessionHandle {
    session: ManagedSession;
    /** Re-fires on status/synced changes. Returns unsubscribe. */
    subscribe(listener: () => void): () => void;
    release(): void;
}
export interface SessionProviderFactory { (opts: {
    url: string; roomName: string; ydoc: Y.Doc; token: () => string;
    onStatus: (s: SessionStatus) => void; onSynced: () => void; onAuthFailed: (reason: string) => void;
    onStateless: (payload: string) => void;
}): SessionProvider; }

interface InternalSession {
    public: ManagedSession;
    viewRefs: number;
    agentRefs: number;
    idleTimer: ReturnType<typeof setTimeout> | null;
    listeners: Set<() => void>;
    /** Each waiter is a `complete(success)` closure that clears its own timeout and removes itself. */
    syncWaiters: Set<(success: boolean) => void>;
    bindingExpiryTimer: ReturnType<typeof setTimeout> | null;
}

const DEFAULT_IDLE_TTL_MS = 120_000;

export class DocumentSessionManager {
    private sessions = new Map<string, InternalSession>();
    private destroyedListeners = new Set<(roomName: string) => void>();

    constructor(private deps: {
        collabUrl: () => string | undefined;
        getToken: () => string;
        providerFactory: SessionProviderFactory;
        idleTtlMs?: number;
    }) {}

    acquireView(parts: RoomParts): SessionHandle | null {
        const s = this.ensure(parts);
        if (!s) return null;
        s.viewRefs += 1;
        return this.makeHandle(s, "view");
    }

    acquireAgent(parts: RoomParts): SessionHandle | null {
        const s = this.ensure(parts);
        if (!s) return null;
        s.agentRefs += 1;
        return this.makeHandle(s, "agent");
    }

    whenSynced(session: ManagedSession, timeoutMs = 15_000): Promise<boolean> {
        const s = this.sessions.get(session.roomName);
        if (!s) return Promise.resolve(false);
        if (s.public.synced) return Promise.resolve(true);
        return new Promise<boolean>(resolve => {
            const complete = (success: boolean) => {
                clearTimeout(timer);
                s.syncWaiters.delete(complete);
                resolve(success);
            };
            const timer = setTimeout(() => complete(false), timeoutMs);
            s.syncWaiters.add(complete);
        });
    }

    onSessionDestroyed(listener: (roomName: string) => void): () => void {
        this.destroyedListeners.add(listener);
        return () => this.destroyedListeners.delete(listener);
    }

    /**
     * True if any live session's provider reports unsynced local changes
     * (see `SessionProvider.hasUnsyncedChanges`). Correct CRDT behavior lets
     * editing continue offline against the cached ydoc — but reloading
     * before reconnect would silently discard that ydoc after the user was
     * told the edit "staged". The singleton wires this into a `beforeunload`
     * prompt (F2).
     */
    hasUnsyncedLocalChanges(): boolean {
        for (const s of this.sessions.values()) {
            if (s.public.provider.hasUnsyncedChanges === true) return true;
        }
        return false;
    }

    /**
     * Drop every Product-bound YDoc after a policy downgrade. Reconnecting
     * starts from server state and prevents buffered writer updates from being
     * replayed under a later read-only session.
     */
    revokeAll(): void {
        [...this.sessions.keys()].forEach(roomName => this.destroy(roomName));
    }

    /**
     * Contract with `providerFactory`: onStatus/onSynced/onAuthFailed must fire
     * asynchronously — never synchronously during construction (true of the real
     * HocuspocusProvider). A synchronous onAuthFailed here would fire before
     * `internal` is registered in `this.sessions`, so destroy() would no-op and
     * the session would then get registered anyway, already dead.
     */
    private ensure(parts: RoomParts): InternalSession | null {
        const url = this.deps.collabUrl();
        if (!url || !this.deps.getToken()) return null;
        const roomName = buildRoomName(parts);
        let s = this.sessions.get(roomName);
        if (s) {
            if (s.idleTimer) { clearTimeout(s.idleTimer); s.idleTimer = null; }
            return s;
        }
        const ydoc = new Y.Doc();
        const internal: InternalSession = {
            public: {
                roomName, ydoc, provider: null as unknown as SessionProvider, status: "connecting",
                synced: false, everSynced: false, trustedBindings: new Map()
            },
            viewRefs: 0, agentRefs: 0, idleTimer: null,
            listeners: new Set(), syncWaiters: new Set(), bindingExpiryTimer: null
        };
        const scheduleBindingExpiry = () => {
            if (internal.bindingExpiryTimer) clearTimeout(internal.bindingExpiryTimer);
            internal.bindingExpiryTimer = null;
            const expiries = [...internal.public.trustedBindings.values()].map(binding => binding.expiresAt);
            if (!expiries.length) return;
            const delay = Math.max(0, Math.min(...expiries) - Date.now());
            internal.bindingExpiryTimer = setTimeout(() => {
                const now = Date.now();
                internal.public.trustedBindings = new Map(
                    [...internal.public.trustedBindings].filter(([, binding]) => binding.expiresAt > now)
                );
                internal.listeners.forEach(listener => listener());
                scheduleBindingExpiry();
            }, delay);
        };
        internal.public.provider = this.deps.providerFactory({
            url, roomName, ydoc,
            token: () => this.deps.getToken(),
            onStatus: status => {
                internal.public.status = status;
                if (status !== "connected") {
                    internal.public.synced = false;
                    internal.public.trustedBindings = new Map();
                    scheduleBindingExpiry();
                    internal.public.provider.setAwarenessField("activityV1", undefined);
                    internal.public.provider.setAwarenessField("sectionField", undefined);
                    internal.public.provider.setAwarenessField("pointer", undefined);
                }
                internal.listeners.forEach(l => l());
            },
            onSynced: () => {
                internal.public.status = "connected";
                internal.public.synced = true;
                internal.public.everSynced = true;
                internal.syncWaiters.forEach(w => w(true));
                internal.syncWaiters.clear();
                internal.listeners.forEach(l => l());
            },
            onAuthFailed: reason => {
                console.warn("[collab] authentication failed:", reason);
                this.destroy(roomName);
            },
            onStateless: payload => {
                const bindings = parseTrustedBindingSnapshot(payload);
                if (!bindings) return;
                internal.public.trustedBindings = bindings;
                scheduleBindingExpiry();
                internal.listeners.forEach(l => l());
            }
        });
        this.sessions.set(roomName, internal);
        return internal;
    }

    private makeHandle(s: InternalSession, kind: "view" | "agent"): SessionHandle {
        let released = false;
        return {
            session: s.public,
            subscribe: listener => {
                s.listeners.add(listener);
                return () => s.listeners.delete(listener);
            },
            release: () => {
                if (released) return;
                released = true;
                if (kind === "view") {
                    s.viewRefs = Math.max(0, s.viewRefs - 1);
                    if (s.viewRefs === 0) {
                        s.public.provider.setAwarenessField("activityV1", undefined);
                        s.public.provider.setAwarenessField("sectionField", undefined);
                        s.public.provider.setAwarenessField("pointer", undefined);
                        s.public.provider.setAwarenessField("user", undefined);
                    }
                } else s.agentRefs = Math.max(0, s.agentRefs - 1);
                this.maybeScheduleDestroy(s);
            }
        };
    }

    private maybeScheduleDestroy(s: InternalSession): void {
        if (s.viewRefs > 0 || s.agentRefs > 0 || s.idleTimer) return;
        s.idleTimer = setTimeout(() => {
            if (s.viewRefs === 0 && s.agentRefs === 0) this.destroy(s.public.roomName);
        }, this.deps.idleTtlMs ?? DEFAULT_IDLE_TTL_MS);
    }

    private destroy(roomName: string): void {
        const s = this.sessions.get(roomName);
        if (!s) return;
        this.sessions.delete(roomName);
        if (s.idleTimer) clearTimeout(s.idleTimer);
        if (s.bindingExpiryTimer) clearTimeout(s.bindingExpiryTimer);
        s.syncWaiters.forEach(w => w(false));
        s.syncWaiters.clear();
        s.listeners.clear();
        this.destroyedListeners.forEach(l => l(roomName));
        s.public.provider.destroy();
        s.public.ydoc.destroy();
    }
}
